import PageBase from '@glittr/frontend-core/src/core/v2/app/pageBase';
import DataSource from '@glittr/frontend-core/src/core/v2/data/data-source';
import Vue from 'vue';
import _ from '@glittr/frontend-core/src/utils';
import { ConfigType } from '@core/plugins/config/config';
import Int64LookupModel from '@glittr/frontend-core/src/services/v2/model/int64-lookup-model';
import { IJSONSchema } from '@glittr/frontend-core/src/plugins/validation/IJSONSchema';
import personalDetailsVueSchema from './registration.vue.schema';
import PostCompanyRequestModel from '../../../../../services/v2/model/post-company-request-model';
import CompanyRequestModel from '../../../../../services/v2/model/company-request-model';
import ContactRequestModel from '../../../../../services/v2/model/contact-request-model';
import UserRequestModel from '../../../../../services/v2/model/user-request-model';
import UploadTdgDocumentRequestModel from '../../../../../services/v2/model/upload-tdg-document-request-model';
import LocationModel from '../../../../../services/v2/model/location-model';
import GetTdgDocumentByExternalIdRequestModel from '../../../../../services/v2/model/get-tdg-document-by-external-id-request-model';
import PutCompanyRequestModel from '../../../../../services/v2/model/put-company-request-model';

export default class CompanyRegistrationPage extends PageBase {
  constructor() {
    super();
    this.combinedModels = new PostCompanyRequestModel();
    this.combinedModels.company = new CompanyRequestModel();
    this.combinedModels.company.location = new LocationModel();
    this.combinedModels.company.location.latitude = this.combinedModels.defaultLatitude;
    this.combinedModels.company.location.longitude = this.combinedModels.defaultLongitude;
    this.combinedModels.company.contact = new ContactRequestModel();
    this.combinedModels.user = new UserRequestModel();
  }

  public get company():PutCompanyRequestModel {
    if (this.existingCompanyDataSource?.data?.data) {
      return this.existingCompanyDataSource?.data?.data;
    }
    const newModel = new PutCompanyRequestModel();
    newModel.companyLocation = new LocationModel({ latitude: 47.1177271111, longitude: 9.1492561111 });
    return newModel;
  }

  public set convertedError(x:any) { /* NOP */ }

  public get convertedError(): any {
    if (_.isObject(this.error)) {
      return {
        ...this.error,
        data: {
          ...this.error.data,
          result: this.error.data.Result,
          info: {
            ...this.error.data.info,
            statusCode: this.error.data.Info?.StatusCode,
            statusMessage: this.error.data.Info?.StatusMessage,
            pagingInfo: this.error.data.Info?.PagingInfo,
            isError: this.error.data.Info?.IsError,
            errorInfo: {
              ...this.error.data.Info?.ErrorInfo,
              details: { ...this.error.data.Info?.ErrorInfo.Details },

            },
          },
        },
      };
    }
    return this.error;
  }

  public error?: any = undefined;

  public emailError: string | null = null;

  public logoDocuments: undefined | File[];

  public isLoading = false;

  public secondPassword: string | undefined;

  public combinedModels:PostCompanyRequestModel;

  public registrationSuccess = false;

  public professionItems: Int64LookupModel[] | undefined = undefined;

  public companyExternalId: string | undefined = undefined;

  public accessToken: string | undefined = undefined;

  public get newCompany(): boolean {
    return !this.companyExternalId;
  }

  public alreadyRegistratedCompany = false;

  public eventIdentifier: string | undefined = undefined;

  public eventName: string | undefined = undefined;

  public userName: string | undefined = undefined;

  public wantsNewUser = false;

  public genderOptions() {
    return [
      { id: 0, caption: Vue.$t('core.address.salutationEnum3') },
      { id: 1, caption: Vue.$t('core.address.salutationEnum1') },
      { id: 2, caption: Vue.$t('core.address.salutationEnum2') },
    ];
  }

  public companiesDataSource = new DataSource({
    insertCommand: Vue.$service.v2.api.tdg_Companies.postCompany,
  });

  public professionsDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Professions.lookupAllProfessions,
  });

  public existingCompanyDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Companies.getCompanyByExternalIdAsync,
  });

  public companyBycompanyIdAndExternalIdDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Companies.getCompanyByExternalIdAndEventIdentifierAsync,
  });

  public eventsDataSource = new DataSource({
    selectCommand: Vue.$service.v2.api.tdg_Events.getEventByEventIdentifier,
  });

  async initialize(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const companyIdParam = urlParams.get('companyid');
    this.companyExternalId = companyIdParam ?? undefined;

    const accessTokenParam = urlParams.get('accesstoken');
    this.accessToken = accessTokenParam ?? undefined;
    if (this.accessToken) {
      this.combinedModels.accessToken = this.accessToken;
    }

    this.eventIdentifier = this.getEventIdentifier('current-eventidentifier') as unknown as string;

    await this.loadProfessionLookup();

    if (this.companyExternalId != null) {
      this.companyBycompanyIdAndExternalIdDataSource.filter.externalId = this.companyExternalId;
      this.companyBycompanyIdAndExternalIdDataSource.filter.eventIdentifier = this.eventIdentifier;
      const company = await this.companyBycompanyIdAndExternalIdDataSource.select();

      this.existingCompanyDataSource.filter.externalId = this.companyExternalId;
      const result = await this.existingCompanyDataSource.select();

      if (result.data?.userName) {
        this.userName = result.data.userName;
      }

      if (company.data == null) {
        if (result.data.logoDocumentExternalId) {
          const requestDocument = new GetTdgDocumentByExternalIdRequestModel();
          requestDocument.externalId = result.data.logoDocumentExternalId;
          const document = await Vue.$service.v2.api.tdg_Documents.getTdgDocumentByExternalId(requestDocument);
          try {
            const fileContent = await Vue.$service.v2.api.tdg_Documents.downloadTdgDocument(requestDocument);
            const file = new File([fileContent.data], document.data.originalFileName || 'unknown Filename', { type: document.data.contentType });
            Vue.set(this, 'logoDocuments', [file]);
          } catch (error) {
            Vue.set(this, 'logoDocuments', []);
          }
        }

        if (result) {
          this.combinedModels = new PostCompanyRequestModel({
            eventIdentifier: this.eventIdentifier,
            accessToken: this.accessToken,
          });
          this.combinedModels.user = new UserRequestModel();
          this.combinedModels.company = new CompanyRequestModel({
            name: result.data.companyName,
            logoDocumentExternalId: result.data.logoDocumentExternalId,
            street: result.data.companyStreet,
            houseNumber: result.data.companyHouseNumber,
            zip: result.data.companyZIP,
            city: result.data.companyCity,
            professionIds: result.data.professionIds,
            website: result.data.website,
            description: result.data.description,
            apprenticesCount: result.data.apprenticesCount,
            apprenticeShipsCount: result.data.apprenticeShipsCount,
            employeesCount: result.data.employeesCount,
            location: {
              latitude: result.data.companyLocation?.latitude,
              longitude: result.data.companyLocation?.longitude,
            },
            acceptConditions: false, // User always needs to accept the conditions
          });
          this.combinedModels.company.contact = new ContactRequestModel({
            salutation: result.data.companyContactSalutation,
            firstName: result.data.companyContactFirstName,
            lastName: result.data.companyContactLastName,
            phoneNumber: result.data.companyContactPhoneNumber,
            email: result.data.companyContactEmail,
          });
        }
      } else {
        this.alreadyRegistratedCompany = true;
        this.eventsDataSource.filter.eventIdentifier = this.eventIdentifier;
        const eventRecord = await this.eventsDataSource.select();

        if (eventRecord?.data?.id !== undefined) {
          this.eventName = eventRecord.data.name;
        }
      }
    }
  }

  public saveNewCompanies = async () => {
    if (this.combinedModels!.user != null) {
      if (this.newCompany) {
        if (this.combinedModels!.user!.email == null) {
          this.combinedModels!.user!.email = this.combinedModels!.company!.contact!.email;
        }
      }
      this.combinedModels!.user!.email = this.combinedModels!.user!.email;
      if (this.combinedModels!.isDefaultLocation) {
        this.combinedModels!.company!.location!.latitude = undefined;
        this.combinedModels!.company!.location!.longitude = undefined;
      }
    }

    const schema = this.getValidationSchema();
    const valid = this.combinedModels!.validateWithSchema(schema);
    if (valid) {
      if (this.combinedModels!.user != null) {
        if (!this.combinedModels!.user || this.combinedModels!.user.repeatPassword !== this.combinedModels!.user.password) {
          this.error = Vue.$t('page.widget.error.passwordsDoNotMatch');
          return;
        }
      }
      const logoFiles = this.logoDocuments ?? [];
      if (logoFiles.length > 0) {
        const request = new UploadTdgDocumentRequestModel();
        request.uploadFile = this.logoDocuments?.[0];
        const documentId = await Vue.$service.v2.api.tdg_Documents.uploadTdgDocument(request);
        this.combinedModels!.company!.logoDocumentExternalId = documentId.data;
      } else {
        this.combinedModels!.company!.logoDocumentExternalId = undefined;
      }
      this.isLoading = true;
      try {
        if (this.combinedModels) {
          this.combinedModels.eventIdentifier = this.eventIdentifier;
          if (this.newCompany === false && this.wantsNewUser === false) {
            this.combinedModels.user = undefined;
          }
          if (this.newCompany === false) {
            this.combinedModels.existingCompanyId = this.companyExternalId;
          }
        }
        await this.companiesDataSource.insert(this.combinedModels!);
        this.registrationSuccess = true;
        this.showExternalElement('widget-successelementid');
        this.isLoading = false;
      } catch (error: any) {
        Vue.$log.error(error);
        this.error = error;
        this.isLoading = false;
      }
    }
  };

  async loadProfessionLookup() {
    try {
      await this.professionsDataSource.select();
      if (this.professionsDataSource.data) {
        this.professionItems = this.professionsDataSource.data.items.map((i) => i.data);
      }
    } catch (error) {
      Vue.$log.error(`Failed to load organisations: ${error}`);
    }
  }

  private getValidationSchema(): IJSONSchema {
    const baseSchema = JSON.parse(JSON.stringify(personalDetailsVueSchema));

    if (this.combinedModels?.company?.name) {
      if (baseSchema.properties) {
        if (!this.wantsNewUser) {
          delete baseSchema.properties.user;
          baseSchema.required = baseSchema.required?.filter((r: string) => r !== 'user');
        }
      }
    }

    return baseSchema;
  }

  public getExternalElement(configKey: keyof ConfigType) {
    const externalElementId = Vue.$config.getQueryOrValue(configKey);
    if (!_.isEmpty(externalElementId)) {
      const externalElement = document.getElementById(externalElementId!);
      if (externalElement === null) {
        Vue.$log.warning(`Element with id '${externalElementId}' not found.`);
      } else {
        return externalElement;
      }
    }
    return undefined;
  }

  public showExternalElement(configKey: keyof ConfigType) {
    const elem = this.getExternalElement(configKey);
    elem?.classList.add('glitWidgetElementVisible');
  }

  public hideExternalElement(configKey: keyof ConfigType) {
    const elem = this.getExternalElement(configKey);
    elem?.classList.remove('glitWidgetElementVisible');
  }

  public getEventIdentifier(configKey: keyof ConfigType) {
    const externalElementId = Vue.$config.getQueryOrValue(configKey);
    return externalElementId;
  }
}
